import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Code, Eye } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { API_URL, jsonAuthHeaders } from '../../utils/api';
import { parsePriceInput, round2 } from '../../utils/pricing';

export default function RoomForm() {
  const { currentUser } = useAuth();
  const { incrementReads, incrementWrites } = useData();
  const [formData, setFormData] = useState({
    name: '',
    features: '',
    adultCapacity: '',
    childCapacity: '',
    price: '',
    childFee: ''
  });
  const [loading, setLoading] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // URL'den id parametresini al (varsa düzenleme modudur)

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const response = await fetch(`${API_URL}/roomTypes/${id}`);
        incrementReads(1);
        if (response.ok) {
          const data = await response.json();
          setFormData({
            name: data.name || '',
            features: data.features || '',
            adultCapacity: data.adultCapacity ?? '',
            childCapacity: data.childCapacity ?? '',
            price: data.price || '',
            childFee: data.childFee ?? ''
          });
        }
      } catch (error) {
        console.error("Hata:", error);
      }
    };

    if (id) {
      fetchRoom();
    }
  }, [id, incrementReads]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Çocuk ücreti metin olarak girilir: "5.000" → 5000 (type=number olsaydı tarayıcı 5 okurdu)
    const rawFee = String(formData.childFee ?? '').trim();
    const childFee = rawFee === '' ? null : round2(parsePriceInput(rawFee));
    if (childFee !== null && (!Number.isFinite(childFee) || childFee < 0)) {
      alert(`Çocuk ücreti geçersiz: "${rawFee}". Ör. 5000 ya da 5.000 yazın; tanımsız bırakmak için boş bırakın.`);
      return;
    }
    if (childFee !== null && childFee > 0 && childFee < 100 &&
        !window.confirm(`Çocuk ücreti ${childFee.toLocaleString('tr-TR')} TL olarak kaydedilecek. Doğru mu? (5.000 TL için "5000" ya da "5.000" yazın.)`)) {
      return;
    }

    setLoading(true);

    try {
      // Sayısal değerleri number tipine çevir
      const dataToSave = {
        ...formData,
        adultCapacity: Number(formData.adultCapacity),
        childCapacity: Number(formData.childCapacity),
        // Boş = tanımsız (teklifte çocuk ücreti eklenmez); DECIMAL kolona '' yazılamaz
        childFee,
        // Varsayılan fiyat artık fiyatlamada kullanılmıyor ve formda yok: mevcut değer korunur, yeni odada boş
        price: formData.price === '' || formData.price === null ? null : formData.price
      };
      // Yazma istekleri token ister (eskiden gönderilmediği için oda kaydı 401 ile başarısız oluyordu)
      const headers = jsonAuthHeaders(currentUser?.token);

      const userInfo = {
        uid: currentUser.uid,
        email: currentUser.email
      };

      if (id) {
        // Güncelleme
        const response = await fetch(`${API_URL}/roomTypes/${id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ ...dataToSave, updatedBy: userInfo, updatedAt: new Date() })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || errorData.sqlMessage || 'Oda güncellenemedi.');
        }
        incrementWrites(2); // Güncelleme + Log
        await fetch(`${API_URL}/activityLogs`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                action: 'Oda Güncellendi',
                description: `"${dataToSave.name}" odası güncellendi.`,
                performedBy: userInfo
            })
        });
      } else {
        // Yeni Ekleme
        const response = await fetch(`${API_URL}/roomTypes`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ ...dataToSave, createdBy: userInfo, createdAt: new Date() })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || errorData.sqlMessage || 'Oda kaydedilemedi.');
        }
        incrementWrites(2); // Ekleme + Log
        await fetch(`${API_URL}/activityLogs`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                action: 'Oda Eklendi',
                description: `"${dataToSave.name}" odası eklendi.`,
                performedBy: userInfo
            })
        });
      }
      navigate('/rooms');
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      alert("Bir hata oluştu: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align'
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/rooms')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {id ? 'Oda Düzenle' : 'Yeni Oda Ekle'}
        </h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Oda Adı
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Örn: Standart Oda"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Yetişkin Kapasitesi
              </label>
              <input
                type="number"
                name="adultCapacity"
                value={formData.adultCapacity}
                onChange={handleChange}
                required
                min="1"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Çocuk Kapasitesi
              </label>
              <input
                type="number"
                name="childCapacity"
                value={formData.childCapacity}
                onChange={handleChange}
                required
                min="0"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Çocuk Ücreti · 7-12 yaş (₺)
              </label>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                name="childFee"
                value={formData.childFee}
                onChange={handleChange}
                placeholder="Tanımsız (ör. 5.000)"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Çocuk başına, her gece eklenir. 0-6 yaş ücretsiz, 13+ yetişkin sayılır.</p>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 md:pt-6">
              Oda fiyatları yetişkin sayısına göre <strong>Fiyat Takvimi</strong>'nden girilir.
              Takvimde fiyatı olmayan gecelerde tekliflere 0 TL gelir.
            </div>
          </div>

          <div className="mb-12">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Oda Özellikleri
              </label>
              <button
                type="button"
                onClick={() => setShowSource(!showSource)}
                className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-md transition-colors"
              >
                {showSource ? <><Eye size={16}/> Önizleme</> : <><Code size={16}/> HTML Kaynak</>}
              </button>
            </div>
            
            {showSource ? (
              <textarea
                value={formData.features}
                onChange={(e) => setFormData(prev => ({ ...prev, features: e.target.value }))}
                className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="HTML kodlarınızı buraya yazabilirsiniz..."
              />
            ) : (
              <div className="dark:text-gray-800 dark:bg-gray-100 rounded-lg">
              <ReactQuill
                theme="snow"
                value={formData.features || ''}
                onChange={(value, delta, source) => {
                  // Yalnızca kullanıcı düzenlemesi: Quill içerik yüklenirken HTML'i normalleştirip onChange tetikliyor
                  if (source !== 'user') return;
                  setFormData(prev => ({ ...prev, features: value }));
                }}
                className="h-64"
                placeholder="Örn: Deniz manzaralı, Balkon, TV..."
                modules={modules}
                formats={formats}
              />
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save size={20} />
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
